/** Pending login context survives the OIDC redirect for post-callback analytics. */
const LOGIN_ANALYTICS_PENDING_KEY = 'buxus.web.login.analytics.pending'

/**
 * Survives React StrictMode remount (refs reset). Prevents double `user_login_attempted`
 * + double IdP redirect from `/login/start`.
 */
const LOGIN_START_OIDC_CLAIM_KEY = 'buxus-web-login-start-oidc-claim-v1'

export type PendingLoginAnalytics = {
  email: string
  mode: 'user' | 'operator'
}

export function storePendingLoginAnalytics(data: PendingLoginAnalytics): void {
  try {
    sessionStorage.setItem(LOGIN_ANALYTICS_PENDING_KEY, JSON.stringify(data))
  } catch {
    /* private mode */
  }
}

export function consumePendingLoginAnalytics(): PendingLoginAnalytics | null {
  try {
    const raw = sessionStorage.getItem(LOGIN_ANALYTICS_PENDING_KEY)
    sessionStorage.removeItem(LOGIN_ANALYTICS_PENDING_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingLoginAnalytics
    if (parsed?.mode !== 'user' && parsed?.mode !== 'operator') return null
    return {
      email: typeof parsed.email === 'string' ? parsed.email : '',
      mode: parsed.mode,
    }
  } catch {
    return null
  }
}

export function clearPendingLoginAnalytics(): void {
  try {
    sessionStorage.removeItem(LOGIN_ANALYTICS_PENDING_KEY)
  } catch {
    /* ignore */
  }
}

/** @returns true if this tab may start OIDC from `/login/start` (first claim wins). */
export function claimLoginStartOidcAttempt(ttlMs = 120_000): boolean {
  try {
    const raw = sessionStorage.getItem(LOGIN_START_OIDC_CLAIM_KEY)
    if (raw) {
      const startedAt = Number(raw)
      if (Number.isFinite(startedAt) && Date.now() - startedAt < ttlMs) {
        return false
      }
    }
    sessionStorage.setItem(LOGIN_START_OIDC_CLAIM_KEY, String(Date.now()))
    return true
  } catch {
    return true
  }
}

export function clearLoginStartOidcClaim(): void {
  try {
    sessionStorage.removeItem(LOGIN_START_OIDC_CLAIM_KEY)
  } catch {
    /* ignore */
  }
}
